import { BorderOutlined, PushpinOutlined } from '@ant-design/icons';
import { EnumTagSemanticoDTOTipo } from '@framework/api-client/api/users/schemas/enumTagSemanticoDTOTipo.ts';
import { type TagSemanticoDTO } from '@framework/api-client/api/users/schemas/tagSemanticoDTO.ts';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { DatatypeIcon } from '@framework/engine/schema/expressions/datatype/DatatypeIcon.tsx';
import { assertNever } from '@framework/engine/utils/assertNever.ts';
import { Space, Tag } from 'antd';
import { useMemo } from 'react';
import styled from 'styled-components';
import {
  deriveSemanticTagMode,
  SemanticTagMode,
} from '../../../../../semantic-tags/deriveSemanticTagMode.ts';

const SemanticTagBadgeSpace = styled(Space)`
  border-radius: 4px;
  padding: 0px 6px;
  max-height: 26px;
`;

/**
 * Mappa il tipo di un tag semantico nel corrispondente `Datatype` (o
 * `'geometry'` per i tipi geografici) usato per scegliere l'icona.
 */
function mapSemanticTagType(
  semanticTagType: EnumTagSemanticoDTOTipo | null | undefined,
) {
  if (!semanticTagType) return null;
  if (semanticTagType === EnumTagSemanticoDTOTipo.GEOMETRY) return 'geometry';
  if (semanticTagType === EnumTagSemanticoDTOTipo.MAPPA) return 'geometry';

  switch (semanticTagType) {
    case EnumTagSemanticoDTOTipo.BOOLEAN:
      return Datatype.Boolean;
    case EnumTagSemanticoDTOTipo.DATE:
      return Datatype.Date;
    case EnumTagSemanticoDTOTipo.NUMERIC:
      return Datatype.Number;
    case EnumTagSemanticoDTOTipo.TEXT:
      return Datatype.String;
    case EnumTagSemanticoDTOTipo.FILE:
      return Datatype.File;
    default:
      assertNever(semanticTagType);
  }
}

export interface ISemanticTagOptionProps {
  semanticTag: TagSemanticoDTO;
}

/**
 * Opzione della select che renderizza il semantic tag con l'icona del tipo di dato.
 */
export function SemanticTagBadge(props: ISemanticTagOptionProps) {
  const { semanticTag } = props;

  const tagType = mapSemanticTagType(semanticTag.tipo);

  const Icon = useMemo(() => {
    if (!tagType) return <BorderOutlined />;
    if (tagType === 'geometry') return <PushpinOutlined />;
    return <DatatypeIcon type={tagType} />;
  }, [tagType]);

  // Allineato alla derivazione del form admin: flMultiplo null/undefined
  // (vecchie configurazioni) trattato come false, non più confronto strict.
  const isUniqueShared =
    deriveSemanticTagMode(semanticTag.flMultiplo, semanticTag.aggregazione) ===
    SemanticTagMode.UNIQUE_SHARED;

  return (
    <SemanticTagBadgeSpace>
      {Icon}
      <span>
        {semanticTag.titolo ?? semanticTag.nome ?? semanticTag.id ?? 'N/A'}
      </span>
      {isUniqueShared && (
        <Tag color="blue" style={{ marginInlineEnd: 0 }}>
          Unico condiviso
        </Tag>
      )}
    </SemanticTagBadgeSpace>
  );
}
